import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import "../styles/Modal.css";

// === ICONS (Tetap Sama) ===
const Icons = {
  success: (
    <svg className="modal-icon success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  finish: (
    <svg className="modal-icon success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  error: (
    <svg className="modal-icon error" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  info: (
    <svg className="modal-icon info" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  warning: (
    <svg className="modal-icon warning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  loading: (
    <svg className="modal-icon loading-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2 12c0-5.523 4.477-10 10-10" />
    </svg>
  )
};

export default function Modal({
  isOpen,
  title,
  message,
  onClose,
  type = "info",
  isLoading = false, // Dukungan untuk property isLoading yang dilempar dari beberapa komponen
  onConfirm,
  confirmOnly = false,
  confirmLabel = "Oke", // Diubah defaultnya agar lebih umum
  cancelLabel = "Batal",
}) {
  const [isRendered, setIsRendered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const confirmBtnRef = useRef(null);

  // Normalisasi Tipe Modal jika ada props isLoading = true
  const activeType = isLoading ? "loading" : type;

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
      // Menunggu animasi CSS selesai sebelum benar-benar di-unmount dari DOM
      const timer = setTimeout(() => setIsRendered(false), 400); 
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Fokuskan tombol saat modal terbuka (aksesibilitas)
  useEffect(() => {
    if (isOpen && confirmBtnRef.current && activeType !== "loading") {
      confirmBtnRef.current.focus();
    }
  }, [isOpen, activeType]);

  const handleClose = () => {
    if (activeType === "loading") return;
    if (onClose) onClose();
  };

  const handleConfirm = async () => {
    if (onConfirm) {
      try {
        await onConfirm();
      } catch (error) {
        console.error("Modal action failed:", error);
      }
    } else {
      handleClose();
    }
  };

  if (!isRendered) return null;

  // Title default jika tidak disediakan
  const defaultTitle = {
    loading: "Memproses...",
    success: "Berhasil!",
    finish: "Selesai",
    error: "Terjadi Kesalahan",
    warning: "Peringatan",
    info: "Informasi",
  }[activeType] || "Informasi";

  // Warna tombol dinamis sesuai tipe (disesuaikan dengan CSS Glass)
  const getBtnClass = () => {
    if (activeType === "success" || activeType === "finish") return "btn-success";
    if (activeType === "error") return "btn-error";
    if (activeType === "warning") return "btn-warning";
    return "btn-info";
  };

  const modalContent = (
    <div
      className={`modal-backdrop ${isVisible ? "show" : ""}`}
      onMouseDown={handleClose}
    >
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()} // Mencegah modal tertutup jika klik di dalam card
      >
        {/* Tombol X pojok Kanan Atas */}
        {activeType !== "loading" && onClose && (
          <button className="modal-close-icon" onClick={handleClose}>
            &times;
          </button>
        )}

        <div className="modal-header-content">
          <div className={`icon-wrapper bg-${activeType}`}>
            {Icons[activeType] || Icons.info}
          </div>
          <h3 className="modal-title">{title || defaultTitle}</h3>
        </div>

        <div className="modal-body">
          <p>{message}</p>
        </div>

        {activeType !== "loading" && (
          <div className="modal-footer">
            {/* 1. Tombol Konfirmasi / OK (Di Kiri) */}
            <button
              ref={confirmBtnRef}
              className={`btn btn-primary ${getBtnClass()}`}
              onClick={handleConfirm}
            >
              {confirmLabel}
            </button>

            {/* 2. Tombol Batal (Di Kanan, muncul hanya jika ada onConfirm dan tidak confirmOnly) */}
            {onConfirm && !confirmOnly && onClose && (
              <button className="btn btn-secondary" onClick={handleClose}>
                {cancelLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Menggunakan Portal untuk menaruh modal di tag <body>
  return ReactDOM.createPortal(modalContent, document.body);
}